import { useCallback, useMemo } from 'react'
import { debounce } from 'lodash'
import { useQuery } from '@apollo/client'
import { DEBOUNCE_DELAY } from 'constants/consts'
import {
  searchQuestionCategory,
  searchQuestionCategory_searchQuestionCategory_items as QuestionCategory,
  searchQuestionCategoryVariables,
} from '__generatedTypes__/searchQuestionCategory'
import { searchQuestionBank, searchQuestionBankVariables } from '__generatedTypes__/searchQuestionBank'
import { searchPracticeTest, searchPracticeTestVariables } from '__generatedTypes__/searchPracticeTest'
import { SEARCH_QUESTION_CATEGORY } from 'queries/questionCategory'
import { SEARCH_PRACTICE_TEST } from 'queries/practiceTest'
import { SEARCH_QUESTION_BANK } from 'queries/questionBanks'

export type QuestionBankOrPracticeTest = { id: string; title: string; type: 'question-bank' | 'practice-test' }

export type QuestionFilters = {
  questionCategory: QuestionCategory
  questionBankOrPracticeTest: QuestionBankOrPracticeTest
}

export type UseQuestionFilters = {
  [key in keyof QuestionFilters]: {
    items: Array<QuestionFilters[key]>
    search: (value: string) => void
    loading: boolean
  }
}

export const useQuestionFilters = (): UseQuestionFilters => {
  const practiceTestsState = useQuery<searchPracticeTest, searchPracticeTestVariables>(SEARCH_PRACTICE_TEST, {
    notifyOnNetworkStatusChange: true,
  })
  const questionBanksState = useQuery<searchQuestionBank, searchQuestionBankVariables>(SEARCH_QUESTION_BANK, {
    notifyOnNetworkStatusChange: true,
  })
  const questionCategoriesState = useQuery<searchQuestionCategory, searchQuestionCategoryVariables>(
    SEARCH_QUESTION_CATEGORY,
    {
      notifyOnNetworkStatusChange: true,
    }
  )

  const practiceTests = practiceTestsState.data?.searchPracticeTest.items ?? []
  const questionBanks = questionBanksState.data?.searchQuestionBank.items ?? []
  const questionCategories = questionCategoriesState.data?.searchQuestionCategory.items ?? []

  const questionBankOrPracticeTests = useMemo(
    () => [
      ...questionBanks.map(({ id, title }) => ({ id, title, type: 'question-bank' } as QuestionBankOrPracticeTest)),
      ...practiceTests.map(({ id, title }) => ({ id, title, type: 'practice-test' } as QuestionBankOrPracticeTest)),
    ],
    [questionBanks, practiceTests]
  )

  const searchQuestionBankOrPracticeTest = useCallback(
    debounce((searchString: string) => {
      questionBanksState.refetch({
        searchString,
      })
      practiceTestsState.refetch({
        searchString,
      })
    }, DEBOUNCE_DELAY),
    []
  )

  const searchQuestionCategory = useCallback(
    debounce((searchString: string) => {
      questionCategoriesState.refetch({
        searchString,
      })
    }, DEBOUNCE_DELAY),
    []
  )

  return {
    questionCategory: {
      items: questionCategories,
      search: searchQuestionCategory,
      loading: questionCategoriesState.loading,
    },
    questionBankOrPracticeTest: {
      items: questionBankOrPracticeTests,
      search: searchQuestionBankOrPracticeTest,
      loading: questionBanksState.loading || practiceTestsState.loading,
    },
  }
}
